import { useEffect, useState } from 'react';
import { Copy, Headphones, Loader2, Mail, RefreshCw, UserMinus, X } from 'lucide-react';
import { FirebaseError } from 'firebase/app';
import toast from 'react-hot-toast';
import {
  coApprovalDjPageUrl,
  coApprovalDjQrUrl,
  createCoApprovalDjSession,
  deleteCoApprovalDjSession,
  saveCoApprovalDjEmail,
  subscribeToCoApprovalDjSession,
  type CoApprovalDjSession,
} from '../../services/coApprovalDj';

interface CoApprovalDjSettingsProps {
  eventId: string;
  djId: string;
}

// Instant actions, like the co-approver: the session doc is the switch, so nothing here joins the save bar (ITER-194)
export function CoApprovalDjSettings({ eventId, djId }: CoApprovalDjSettingsProps) {
  const [session, setSession] = useState<CoApprovalDjSession | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    return subscribeToCoApprovalDjSession(eventId, djId, next => {
      setSession(next);
      setLoaded(true);
    });
  }, [eventId, djId]);

  useEffect(() => {
    setEmailDraft(session?.email ?? '');
  }, [session?.id, session?.email]);

  const pageUrl = session ? coApprovalDjPageUrl(eventId, session.token) : '';
  const emailDraftClean = emailDraft.trim().toLowerCase();
  const linkAlreadySent = emailDraftClean === (session?.email ?? null) && session?.linkEmailStatus === 'sent';

  const saveEmail = async (email: string | null) => {
    setEmailSending(true);
    try {
      await saveCoApprovalDjEmail(eventId, email);
    } catch (saveError) {
      console.error('Co-approval DJ email save failed', saveError);
      if (saveError instanceof FirebaseError && saveError.code === 'functions/invalid-argument') {
        toast.error('Enter a valid email address');
      } else {
        toast.error("Couldn't save the co-approval DJ's email. Try again.");
      }
    } finally {
      setEmailSending(false);
    }
  };

  const handleAdd = async () => {
    setBusy(true);
    try {
      await createCoApprovalDjSession(eventId, djId);
      toast.success('Co-approval DJ link created');
    } catch (addError) {
      console.error('Co-approval DJ link create failed', addError);
      toast.error('Failed to add a co-approval DJ');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!session) return;
    setBusy(true);
    const emailToCarryOver = session.email ?? null;
    try {
      await deleteCoApprovalDjSession(session.id);
      await createCoApprovalDjSession(eventId, djId);
      toast.success('Co-approval DJ link regenerated. Previous link is now invalid.');
    } catch (regenerateError) {
      console.error('Co-approval DJ link regenerate failed', regenerateError);
      toast.error('Failed to regenerate the co-approval DJ link');
      setBusy(false);
      return;
    }
    setBusy(false);
    // The new session starts with no email, so this call carries the address over and emails the new link
    if (emailToCarryOver) await saveEmail(emailToCarryOver);
  };

  const handleRemove = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await deleteCoApprovalDjSession(session.id);
      toast.success('Co-approval DJ removed. Their link no longer works.');
    } catch (removeError) {
      console.error('Co-approval DJ remove failed', removeError);
      toast.error('Failed to remove the co-approval DJ');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(pageUrl)
      .then(() => toast.success('Co-approval DJ link copied!'))
      .catch(() => toast.error('Could not copy the link'));
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Headphones className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Co-approval DJ</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              A trusted DJ who approves requests from their own device. Their approval counts as yours. They can match
              links, run the AI check and save links to your library. They can't change the queue or settings.
            </p>
          </div>
        </div>
        {loaded && !session && (
          <button onClick={handleAdd} disabled={busy} className="btn btn-secondary text-sm flex-shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add a co-approval DJ'}
          </button>
        )}
      </div>

      {session && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {session.helperName
              ? <><span className="font-medium">{session.helperName}</span> is your co-approval DJ.</>
              : "Waiting for your co-approval DJ to open the link and enter their name."}
          </p>

          <div className="flex items-start gap-4">
            <button
              onClick={() => setShowQr(true)}
              className="w-24 h-24 bg-white rounded-lg border-2 border-indigo-200 dark:border-indigo-700 overflow-hidden hover:shadow-lg transition-shadow flex-shrink-0"
              title="Click to enlarge"
            >
              <img src={coApprovalDjQrUrl(eventId, session.token)} alt="Co-approval DJ QR Code" className="w-full h-full object-cover" />
            </button>
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-xs font-medium text-indigo-700 dark:text-indigo-400 uppercase tracking-wide">
                Share this link, or email it below
              </p>
              <div className="flex flex-wrap gap-2">
                <button onClick={copyLink} className="btn btn-secondary text-sm">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy Link
                </button>
                <button onClick={handleRegenerate} disabled={busy} className="btn btn-secondary text-sm">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Regenerate
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="coApprovalDjEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Co-approval DJ's email
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                id="coApprovalDjEmail"
                type="email"
                value={emailDraft}
                onChange={(event) => setEmailDraft(event.target.value)}
                placeholder="name@example.com"
                className="input flex-1 min-w-[12rem]"
              />
              <button
                onClick={() => saveEmail(emailDraftClean)}
                disabled={emailSending || !emailDraftClean || linkAlreadySent}
                className="btn btn-secondary text-sm"
              >
                {emailSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                Send link
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              They get the link by email, then one email each time songs start waiting for review.
            </p>
            {session.email && session.linkEmailStatus === 'sent' && (
              <p className="text-xs text-green-700 dark:text-green-400">Link emailed to {session.email}.</p>
            )}
            {session.email && session.linkEmailStatus === 'skipped' && (
              <p className="text-xs text-amber-700 dark:text-amber-400">No email was sent: email isn't set up in this environment.</p>
            )}
            {session.email && session.linkEmailStatus === 'failed' && (
              <p role="alert" className="text-xs text-red-600 dark:text-red-400">The email couldn't be sent. Copy the link and send it yourself.</p>
            )}
            {session.email && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {session.alertsEnabled !== false ? 'Alert emails: On' : 'Alert emails: Off (switched off by your co-approval DJ)'}
                <button
                  onClick={() => saveEmail(null)}
                  disabled={emailSending}
                  className="ml-3 text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                >
                  Remove email
                </button>
              </p>
            )}
          </div>

          <button onClick={handleRemove} disabled={busy} className="btn text-sm text-danger hover:bg-danger/10 flex items-center gap-1.5">
            <UserMinus className="w-4 h-4" />
            Remove co-approval DJ
          </button>
        </div>
      )}

      {showQr && session && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowQr(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full" onClick={event => event.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Co-approval DJ QR</h3>
              <button onClick={() => setShowQr(false)} className="btn btn-ghost btn-sm btn-circle">
                <X className="w-4 h-4" />
              </button>
            </div>
            <img src={coApprovalDjQrUrl(eventId, session.token)} alt="Co-approval DJ QR Code" className="w-full" />
          </div>
        </div>
      )}
    </div>
  );
}
